import type { PrV2CommentResponse } from './types';

import { isPendingGithubCommentId } from './pending-comment-ids';
import { asRecord } from './utils/as-record';

export type PrV2CommentLifecycle = 'queued' | 'submitting' | 'persisted';

/**
 * Carries the local queued-comment identity across the queued -> submitting handoff.
 * The diff uses this to treat a promoted optimistic comment as the same UI entity
 * instead of briefly dropping the queued row and mounting a new optimistic one.
 */
export function getPrV2CommentTransitionKey(
  comment: Pick<PrV2CommentResponse, 'raw'>,
): string | null {
  const raw = asRecord(comment.raw);
  return typeof raw?.local_transition_key === 'string' && raw.local_transition_key.length > 0
    ? raw.local_transition_key
    : null;
}

export function getPrV2CommentLifecycle(
  comment: Pick<PrV2CommentResponse, 'githubCommentId' | 'raw'>,
): PrV2CommentLifecycle {
  if (isPendingGithubCommentId(comment.githubCommentId)) {
    return 'queued';
  }

  const raw = asRecord(comment.raw);
  if (raw?.is_pending_local === true) {
    return 'queued';
  }

  if (raw?.is_optimistic === true) {
    return 'submitting';
  }

  return 'persisted';
}

export function isQueuedPrV2Comment(
  comment: Pick<PrV2CommentResponse, 'githubCommentId' | 'raw'>,
): boolean {
  return getPrV2CommentLifecycle(comment) === 'queued';
}

export function isSubmittingPrV2Comment(
  comment: Pick<PrV2CommentResponse, 'githubCommentId' | 'raw'>,
): boolean {
  return getPrV2CommentLifecycle(comment) === 'submitting';
}
